import { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import Intercom from '@intercom/messenger-js-sdk';
import * as Sentry from '@sentry/react';

import './App.css';
import { Outlet } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import client from './utils/react-query/client';
import { AppProvider } from './context/AppContext';
import { captureWaitlistArrival } from './utils/waitlistTracking';
import { scrubSentryEvent } from './utils/sentryScrub';
import { IS_PREVIEW } from './context/BookingSiteTheme/preview';

import 'react-toastify/dist/ReactToastify.css';

const environment =
  window.location.hostname === 'web.getporter.io'
    ? 'production'
    : 'development';

// Revenue-critical flows keep a higher trace rate; everything else runs at a
// low baseline — the org's span quota can't afford 100% sampling.
const HIGH_VALUE_TRANSACTIONS = [
  /^\/booking(\/|$)/, // booking/:appointmentId, booking/
  /^\/reschedule\//,
  /^\/checkout(\/|$)/, // /checkout, /checkout/:paymentId
  /^\/receipt\//,
  /^\/payment-redirect/,
  /\/check-in$/, // shop/:shopUrl/check-in
  /\/waiver-session\//, // shop/:shopUrl/waiver-session/:sessionId
  /\/preview-waiver$/,
  /\/request$/, // shop/:shopUrl/request
  /\/service\/[^/]+\/book$/, // shop/:shopUrl/service/:serviceId/book
];

// Session replay only on chat pages, where TalkJS stalls are being diagnosed;
// the integration is fetched from Sentry's CDN so other pages don't carry it.
const isChatPage = /^\/chat(\/|$)/.test(window.location.pathname);

Sentry.init({
  dsn: 'https://85234bff49d2564fccf708730e9b3659@o4509566870487040.ingest.us.sentry.io/4509566871601152',
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  // Tracing — transaction names are raw URL paths (no router instrumentation)
  tracesSampler: ({ name }) =>
    HIGH_VALUE_TRANSACTIONS.some(route => route.test(name)) ? 0.3 : 0.04,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/getporter\.io\/api/],
  environment,
  replaysSessionSampleRate: isChatPage ? 0.1 : 0,
  replaysOnErrorSampleRate: isChatPage ? 1 : 0,
  // Injected at Docker build time (Cloud Build commit SHA); undefined locally.
  release: process.env.REACT_APP_SENTRY_RELEASE,
  enabled: process.env.NODE_ENV !== 'development',
  // Browser/WebView environment noise, not Porter faults. Chunk-load failures
  // are deliberately NOT here — they can signal a bad deploy (see beforeSend).
  ignoreErrors: [
    // Native-bridge probing when the page runs outside the expected WebView
    /window\.webkit\.messageHandlers/,
    'Java object is gone',
    // Page teardown mid-request
    'Promise was rejected because the browsing context is going away',
    // Stripe.js blocked by ad-blockers/network — surfaced to the user already
    'Failed to load Stripe.js',
    // Browser extension messaging
    /runtime\.sendMessage/,
    // Browser-evicted IndexedDB state
    'Connection to Indexed Database server lost',
    'Database deleted by request of the user',
  ],
  beforeSend: event => {
    const scrubbed = scrubSentryEvent(event);
    if (scrubbed) {
      // Group all chunk-load failures into one issue: each deploy's chunk ids
      // differ, so default grouping fragments them and hides rate spikes.
      const message =
        scrubbed.exception?.values?.[0]?.value ?? scrubbed.message ?? '';
      if (/Loading (CSS )?chunk .+ failed/i.test(message)) {
        scrubbed.fingerprint = ['chunk-load-failure'];
      }
    }
    return scrubbed;
  },
});

Sentry.setTag('app', 'customer-web');

if (isChatPage && process.env.NODE_ENV !== 'development') {
  Sentry.lazyLoadIntegration('replayIntegration')
    .then(replayIntegration => Sentry.addIntegration(replayIntegration()))
    .catch(() => {
      // replay is diagnostics only; never block the page on it
    });
}

function App() {
  // Not while the customizer is embedding this page: the bubble would float
  // over the preview card, and a support conversation cannot start from one.
  // Same rule the analytics loader in index.html already follows.
  if (!IS_PREVIEW) {
    Intercom({
      app_id: 'nchtk2tv',
    });
  }

  useEffect(() => {
    captureWaitlistArrival();
  }, []);

  return (
    <div className="App">
      <QueryClientProvider client={client}>
        <AppProvider>
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Outlet />
        </AppProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
